import { useLocationPathname } from '@rocketlane/rocket-router';
import React, { Suspense, useMemo } from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import { PageLoading } from 'components/atoms/loading';
import {
  AgentGlobalResetStyles,
  AgentGlobalStyles,
} from 'components/styles/components/agent-global-styles';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useAgentBuilderHostAPI } from 'hooks/use-agent-builder-host-api';
import { useIsKnowledgeBaseFeatureEnabled } from 'hooks/use-knowledge-base-feature';
import { useAccountDataQuery } from 'queries/account';
import { NotFound } from 'routes/404';
import type { HostAPI } from 'types/host-components';

/** Document agent permissions passed to Agent Builder remote (must match agentBuilder/AgentBuilderApp props) */
type AgentBuilderPermissions = {
  documentAgent: {
    read: boolean;
    write: boolean;
    delete: boolean;
    create: boolean;
    invoke: boolean;
  };
  meetingPageTemplates: {
    read: boolean;
    write: boolean;
    delete: boolean;
    create: boolean;
  };
  emailPageTemplates: {
    read: boolean;
    write: boolean;
    delete: boolean;
    create: boolean;
  };
  nitroAnalyst: {
    create: boolean;
  };
  signalsAnalyst: {
    create: boolean;
  };
};

const AgentBuilderApp = React.lazy<
  React.ComponentType<{
    hostAPI?: HostAPI;
    permissions?: AgentBuilderPermissions;
    hasFeatures?: {
      documentationAgent?: boolean;
      scarlet?: boolean;
      knowledgeBase?: boolean;
      nitroAnalyst?: boolean;
      signalsAnalyst?: boolean;
      migrationAgent?: boolean;
      workforceAgent?: boolean;
    };
  }>
>(() =>
  import('agentBuilder/AgentBuilderApp').then((module) => ({
    default: module.AgentBuilderApp,
  })),
);

const AgentBuilderWrapper = styled.div<{ $isAgentBuilderPath: boolean }>`
  width: 100%;
  height: 100%;
  ${AgentGlobalResetStyles};
`;

export const AgentBuilderRemote: React.FC = () => {
  const pathname = useLocationPathname();
  const { hasAccessToResource } = useWithAccess();
  const { data: accountData } = useAccountDataQuery();
  const features = accountData?.accountMeta?.features;
  const isScarletEnabled = features?.scarlet ?? false;
  const isKnowledgeBaseEnabled = useIsKnowledgeBaseFeatureEnabled();
  const [isProjectGovernancePoliciesEnabled] = useAccountFeature(
    AccountFeature.PolicyEvaluation,
  );
  const [isTimesheetPoliciesEnabled] = useAccountFeature(
    AccountFeature.TimesheetPolicies,
  );
  const [isMigrationAgentFeatureEnabled] = useAccountFeature(
    AccountFeature.MigrationAgent,
  );
  const [isWorkforceAgentFeatureEnabled] = useAccountFeature(
    AccountFeature.WorkforceAgent,
  );
  const hasFeatures = useMemo(
    () => ({
      documentationAgent: features?.documentationAgent,
      scarlet: features?.scarlet,
      knowledgeBase: isKnowledgeBaseEnabled,
      migrationAgent: isMigrationAgentFeatureEnabled,
      workforceAgent: isWorkforceAgentFeatureEnabled,
      nitroAnalyst: features?.nitroAnalyst,
      signalsAnalyst: features?.signalsAnalyst,
      timesheetPolicies: features?.timesheetPolicies,
      policyEvaluation: features?.policyEvaluation,
    }),
    [
      features?.documentationAgent,
      features?.scarlet,
      isKnowledgeBaseEnabled,
      features?.nitroAnalyst,
      features?.signalsAnalyst,
      features?.timesheetPolicies,
      features?.policyEvaluation,
      isMigrationAgentFeatureEnabled,
      isWorkforceAgentFeatureEnabled,
    ],
  );

  const permissions = useMemo(
    () => ({
      documentAgent: {
        read: hasAccessToResource({ rule: 'DOCUMENT_AGENT:READ' }),
        write: hasAccessToResource({ rule: 'DOCUMENT_AGENT:WRITE' }),
        delete: hasAccessToResource({ rule: 'DOCUMENT_AGENT:DELETE' }),
        create: hasAccessToResource({ rule: 'DOCUMENT_AGENT:CREATE' }),
        invoke: hasAccessToResource({ rule: 'DOCUMENT_AGENT:CREATE' }),
      },
      meetingPageTemplates: {
        read: hasAccessToResource({ rule: 'MEETING_PAGE_TEMPLATE:READ' }),
        write: hasAccessToResource({ rule: 'MEETING_PAGE_TEMPLATE:WRITE' }),
        delete: hasAccessToResource({ rule: 'MEETING_PAGE_TEMPLATE:DELETE' }),
        create: hasAccessToResource({ rule: 'MEETING_PAGE_TEMPLATE:CREATE' }),
      },
      emailPageTemplates: {
        read: hasAccessToResource({ rule: 'EMAIL_PAGE_TEMPLATE:READ' }),
        write: hasAccessToResource({ rule: 'EMAIL_PAGE_TEMPLATE:WRITE' }),
        delete: hasAccessToResource({ rule: 'EMAIL_PAGE_TEMPLATE:DELETE' }),
        create: hasAccessToResource({ rule: 'EMAIL_PAGE_TEMPLATE:CREATE' }),
      },
      nitroAnalyst: {
        create: hasAccessToResource({ rule: 'AI_ANALYST:CREATE' }),
      },
      signalsAnalyst: {
        create: hasAccessToResource({ rule: 'SIGNAL_ANALYST:CREATE' }),
      },
    }),
    [hasAccessToResource],
  );

  const agentBuilderSubPaths = [
    '/create',
    '/edit',
    '/livepreview',
    '/experts/',
    '/migration-agents/',
  ];
  const excludedAgentBuilderSubPaths = ['/migration-agents/create'];
  const isAgentBuilderPath =
    pathname.startsWith('/agent-builder') &&
    agentBuilderSubPaths.some((subPath) => pathname.includes(subPath)) &&
    !excludedAgentBuilderSubPaths.some((subPath) => pathname.includes(subPath));

  const hostAPI = useAgentBuilderHostAPI();

  // Block access if user doesn't have permission
  // Requires both documentationAgent feature and DOCUMENT_AGENT:READ (when Scarlet is not enabled)
  const canAccessDocumentAgent =
    hasFeatures.documentationAgent && permissions.documentAgent.read;
  const canAccessMigrationAgent =
    isMigrationAgentFeatureEnabled &&
    hasAccessToResource({ rule: 'MIGRATION_AGENT:READ' });
  const canAccessWorkforceAgent =
    isWorkforceAgentFeatureEnabled &&
    hasAccessToResource({ rule: 'WORKFORCE_AGENT:READ' });

  const isProjectGovernancePoliciesVisible =
    isProjectGovernancePoliciesEnabled &&
    hasAccessToResource({
      rule: 'PROJECT_GOVERNANCE_POLICIES:CREATE',
    });
  const isTimesheetPoliciesVisible =
    isTimesheetPoliciesEnabled &&
    hasAccessToResource({
      rule: 'TIMESHEET_POLICIES:CREATE',
    });

  if (
    !isScarletEnabled &&
    !canAccessDocumentAgent &&
    !canAccessMigrationAgent &&
    !canAccessWorkforceAgent &&
    !isProjectGovernancePoliciesVisible &&
    !isTimesheetPoliciesVisible
  ) {
    return <NotFound />;
  }

  return (
    <Suspense fallback={<PageLoading />}>
      <StyleSheetManager target={document.head}>
        <AgentGlobalStyles />
        <AgentBuilderWrapper
          className="cp-pt-2 cp-pb-2 cp-pl-0 cp-pr-2"
          $isAgentBuilderPath={isAgentBuilderPath}
        >
          <AgentBuilderApp
            hostAPI={hostAPI}
            permissions={permissions}
            hasFeatures={hasFeatures}
          />
        </AgentBuilderWrapper>
      </StyleSheetManager>
    </Suspense>
  );
};
